import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import Select from "../../components/ReportSelect";
import { saveAs } from "file-saver";
import { Container, Table, Button, Row, Col, Spinner } from "react-bootstrap";
import Pagination from "../../components/Pagination";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import { filterReportRows } from "../../utils/reportSearch";
import "../../styles/Reports.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;
const today = dayjs().format("YYYY-MM-DD");
const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD");

const fmt = (d) => (d ? dayjs(d).format("YYYY-MM-DD HH:mm:ss") : "");
const toISO = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "");

export default function HiringCycleData() {
  const navigate = useNavigate();

  // Dropdown lists
  const [orgUnits, setOrgUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [groups, setGroups] = useState([]); // ⭐ NEW: Groups dropdown
  const [statuses, setStatuses] = useState([]);

  // Data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Page filters
  const [pStartDate, setPStartDate] = useState(oneMonthAgo);
  const [pEndDate, setPEndDate] = useState(today);
  const [pOrgUnit, setPOrgUnit] = useState(null);
  const [pGroup, setPGroup] = useState(null); // ⭐ NEW
  const [pCategory, setPCategory] = useState(null);
  const [pSubCat, setPSubCat] = useState(null);
  const [pStatus, setPStatus] = useState(null);
  const [pSearch, setPSearch] = useState("");

  const [filters, setFilters] = useState({});

  // LOAD DROPDOWNS
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    async function load() {
      try {
        const [ou, cat, sc, grp, st] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/position-groups`, { headers }), // ⭐ NEW
          axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
        ]);

        setOrgUnits(ou.data);
        setCategories(cat.data);
        setSubCats(sc.data);
        setGroups(
          grp.data.map((g) => ({
            value: g.pg_id,
            label: g.pg_name,
          }))
        );

        setStatuses(
          st.data.map((s) => ({
            value: s.s_id,
            label: s.s_TranslatedText,
          }))
        );
      } catch (err) {
        console.error("Dropdown load error:", err);
      }
    }

    load();
  }, []);

  // FETCH DATA
  const fetchRows = useCallback(async () => {
    if (!hasSearched) return;

    setLoading(true);
    setCurrentPage(1);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(`${BASE_URL}/api/reports/hiring-cycle-data`, {
        headers,
        params: filters,
      });

      setRows(res.data || []);
    } catch (err) {
      console.error("Hiring Cycle Data fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, hasSearched]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // APPLY FILTERS
  const applyFilters = () => {
    setHasSearched(true);
    setFilters({
      startDate: pStartDate,
      endDate: pEndDate,
      orgUnit: pOrgUnit?.value || "",
      group: pGroup?.value || "", // ⭐ NEW FILTER
      category: pCategory?.value || "",
      subCategory: pSubCat?.value || "",
      status: pStatus?.value || "",
    });
  };

  // CLEAR FILTERS
  const clearFilters = () => {
    setPStartDate(oneMonthAgo);
    setPEndDate(today);
    setPOrgUnit(null);
    setPGroup(null); // ⭐ NEW
    setPCategory(null);
    setPSubCat(null);
    setPStatus(null);
    setPSearch("");

    setFilters({});
    setRows([]);
    setHasSearched(false);
  };

  // Pagination
  const searchedRows = filterReportRows(rows, pSearch);
  const paginated = searchedRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // EXPORT CSV
  const exportCSV = () => {
    if (!hasSearched || searchedRows.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Name",
      "Email",
      "Org Unit",
      "Emp Category",
      "Application Start",
      "Application Completed",
      "Current Status",
      "Status Updated Date",
    ];

    const lines = searchedRows.map((r) =>
      [
        r.name,
        r.email,
        r.org_unit,
        r.emp_category,
        fmt(r.application_start),
        fmt(r.application_completed),
        r.current_status,
        fmt(r.current_status_date),
      ]
        .map((c) => `"${String(c || "").replace(/"/g, '""')}"`)
        .join(",")
    );

    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    saveAs(blob, "HiringCycleData.csv");
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-history" /></span><span>Hiring Cycle Data (Status History)</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Filters */}
      <ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            value={pStartDate}
            onChange={(e) => setPStartDate(toISO(e.target.value))}
          />
        </Col>

        <Col>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            value={pEndDate}
            onChange={(e) => setPEndDate(toISO(e.target.value))}
          />
        </Col>

        <Col>
          <label>Org Unit</label>
          <Select
            options={orgUnits.map((o) => ({
              value: o.ou_id,
              label: o.ou_name,
            }))}
            value={pOrgUnit}
            onChange={setPOrgUnit}
          />
        </Col>

        <Col>
          <label>Group</label>
          <Select options={groups} value={pGroup} onChange={setPGroup} />
        </Col>

        <Col>
          <label>Status</label>
          <Select options={statuses} value={pStatus} onChange={setPStatus} />
        </Col>
      </Row>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Employment Category</label>
          <Select
            options={categories.map((c) => ({
              value: c.c_id,
              label: c.c_name,
            }))}
            value={pCategory}
            onChange={setPCategory}
          />
        </Col>

        <Col>
          <label>Employment Sub-category</label>
          <Select
            options={subCats.map((sc) => ({
              value: sc.sc_id,
              label: sc.sc_name,
            }))}
            value={pSubCat}
            onChange={setPSubCat}
          />
        </Col>

      </Row>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Search</label>
          <input type="text" className="form-control" placeholder="ID / Name / Keyword" value={pSearch} onChange={(event) => setPSearch(event.target.value)} />
        </Col>
      </Row>
      </ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">

        <Col md="auto">
          <div className="button-group">
            <Button variant="primary" onClick={applyFilters}>
              <i className="fas fa-filter" aria-hidden="true" /> Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>

        <Col md="auto" className="ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || searchedRows.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="compact-table">
            <thead className="table-dark text-center">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Org Unit</th>
                <th>Emp Category</th>
                <th>Application Start</th>
                <th>Application Completed</th>
                <th>Current Status</th>
                <th>Status Updated Date</th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    No results found with current filters.
                  </td>
                </tr>
              ) : (
                paginated.map((r, i) => (
                  <tr key={i}>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                    <td>{r.org_unit}</td>
                    <td>{r.emp_category}</td>
                    <td>{fmt(r.application_start)}</td>
                    <td>{fmt(r.application_completed)}</td>
                    <td>{r.current_status}</td>
                    <td>{fmt(r.current_status_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={searchedRows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
